/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module UpdateModule */

import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Type,
} from '@angular/core';

import {
    NodeType,
    UpgradeFsmState,
    UpgradeFsmUiState,
} from 'generated-types';

import { DialogService } from 'ng/modules/core/services/dialog.service';
import { intersection } from 'underscore';
import { ITUpgradeNode } from 'ajs/modules/upgrade/factories/upgrade-node.item.factory';
import { L10nService } from '@vmw/ngx-vip';
import { Subject, takeUntil } from 'rxjs';
import { UpgradeNodeCollection } from 'ajs/modules/upgrade/factories/upgrade-node-collection.factory';

import { IPrecheckModalConfig, UpdateStore } from '../../update.store';
import { UPGRADE_STATE_POLLING_INTERVAL } from '../../../services/update.service';
import { UpgradeTranscriptModalComponent } from '../../upgrade-transcript-modal/upgrade-transcript-modal.component';

import {
    fipsOperationalHash,
    IPrecheckProgressConfig,
    IUserSelectedOptions,
    nodeTypesHash,
    UpdateWizardStage,
    UpgradeStateGroupTypesHash,
} from '../../../update.types';

import * as l10n from './update-progress-card.l10n';
import './update-progress-card.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Type of UpgradeNodeCollection.
 */
type TUpgradeNodeCollection = typeof UpgradeNodeCollection;

/**
 * IDs of modals used in the component.
 */
const UPGRADE_TRANSCRIPT_DIALOG_ID = 'UPGRADE_TRANSCRIPT_DIALOG_ID';

/**
 * Type of params passed to instantiate UpgradeNodeCollection.
 */
interface IUpgradeNodeCollectionParams {
    state?: UpgradeFsmUiState | UpgradeFsmState,
    node_type?: string,
    pre_check_info?: boolean,
    dryrun?: boolean,
}

/**
 * @description Component to show pre checks and actual upgrade progress.
 * @author Sarthak Kapoor, Dhanashree Tilak, Deepak Chaudhary
 */
@Component({
    selector: 'update-progress-card',
    templateUrl: './update-progress-card.component.html',
})
export class UpdateProgressCardComponent implements OnInit, OnDestroy {
    /**
     * FIPS operation flag.
     */
    @Input()
    public fipsOperational?: string;

    /**
     * Input to indicate if the upgrade is for controller.
     */
    @Input()
    public isControllerUpgrade = false;

    /**
     * Input to indicate the selected SE group IDs.
     */
    @Input()
    public selectedSegIds: string[] = [];

    /**
     * Flag to keep track of if the wizard is open.
     */
    @Input()
    public isWizardOpen = false;

    /**
     * Event emitter to share Pre-check current status.
     */
    @Output()
    public checkWizardStatus = new EventEmitter<IPrecheckProgressConfig>();

    /**
     * Event emitter to open the progress modal. This modal displays the upgrade/rollback
     * progress transcript.
     */
    @Output()
    public onOpenProgress = new EventEmitter<{ nodeId: string, nodeType: NodeType }>();

    /**
     * A hash of all possible FIPS operational values.
     */
    public readonly fipsOperationalHash = fipsOperationalHash;

    /**
     * A label which is displayed on the left side of the SE Group
     * progress card.
     */
    public seGroupLeftLabel = '';

    /**
     * Version, controller is upgrading to.
     */
    public controllerVersion = '';

    /**
     * Version, SEG is upgrading to.
     */
    public segVersion: string;

    /**
     * Progress percentage of controller upgrade.
     */
    public controllerUpgradePercentage = 0;

    /**
     * Flag to indicate if controller upgrade is in progress.
     */
    public isControllerUpgradeInProgress = false;

    /**
     * Flag to indicate if SE Group upgrade is in progress.
     */
    public isSeGroupUpgradeInProgress = false;

    /**
     * Flag to indicate if no upgrade is in progress.
     */
    public isNoUpgradeInProgress = true;

    /**
     * Count of SE Groups upgrade in progress.
     */
    public seGroupCount = 0;

    /**
     * Flag to indicate if Pre-checks are in progress.
     */
    public preChecksInProgress = false;

    /**
     * Flag to indicate if SE Group upgrade is selected.
     */
    public isSegUpgradeSelected = false;

    /**
     * L10n keys for the component.
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Flag to track if the flow is of type Dry run.
     */
    public isDryRunFlow = false;

    /**
     * Instance of UpgradeNodeCollection to fetch the upgrade status.
     */
    private upgradeNodeCollection: UpgradeNodeCollection = null;

    /**
     * Options selected while triggering upgrade operation.
     */
    private userSelectedOptions: IUserSelectedOptions;

    /**
     * Flag to track if the flow is of type restore.
     */
    public isRestoreFlow = false;

    /**
     * Version, controller is being rollback to.
     * In case of rollback from a patch upgrade to a controller version, backend doesn't
     * send any imageRef.
     */
    private rollbackVersion: string;

    /**
     * Subject used to unsubscribe observables.
     */
    private ngDestroyed$ = new Subject();

    private isPreCheckFlow = false;

    constructor(
        private readonly l10nService: L10nService,
        @Inject('UpgradeNodeCollection')
        private readonly UpgradeNodeCollectionClass: TUpgradeNodeCollection,
        private readonly dialogService: DialogService,
        private readonly updateStore: UpdateStore,
    ) {
        this.initUpgradeNodeCollection();
        this.l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        const { updateStore } = this;

        this.triggerUpgradeStatusInfoPolling();

        updateStore
            .userSelectedOptions$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((userSelectedOptions: IUserSelectedOptions) => {
                const { applyToSystem } = userSelectedOptions;

                this.userSelectedOptions = userSelectedOptions;
                this.isSegUpgradeSelected = applyToSystem;
            });

        updateStore
            .isPreCheckFlow$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(
                async(isPreCheckFlow: boolean) => {
                    this.isPreCheckFlow = isPreCheckFlow;

                    if (this.isPreCheckFlow && !this.preChecksInProgress) {
                        await this.triggerUpgradeStatusInfoPolling(isPreCheckFlow);
                        await this.updateOverallPreChecksState();
                    }
                },
            );

        updateStore
            .preChecksTriggered$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(
                (preChecksTriggered: boolean) => {
                    this.preChecksInProgress = preChecksTriggered;

                    if (preChecksTriggered) {
                        this.onPrechecksTrigger();
                    }
                },
            );

        updateStore
            .isUpdateTriggeredWithWarning$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((isUpdateTriggeredWithWarning: boolean) => {
                this.onUpdate(isUpdateTriggeredWithWarning);
            });

        updateStore
            .rollbackVersion$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(
                version => this.rollbackVersion = version,
            );

        updateStore
            .preCheckTranscriptOpen$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((config: IPrecheckModalConfig) => {
                this.sharePreChecksProgressToWizard(config.readonly, config.nodeIds);
            });

        updateStore
            .isRestoreFlow$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(isRestoreFlow => this.isRestoreFlow = isRestoreFlow);

        updateStore
            .isDryRunFlow$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(isDryRunFlow => {
                this.isDryRunFlow = isDryRunFlow;

                if (this.isDryRunFlow && !this.isPreCheckFlow) {
                    this.triggerUpgradeStatusInfoPolling(false);
                }
            });
    }

    /**
     * @override
     * Reset the pre-checks triggered state in case they are still in progress
     * and user navigates away from the page.
     */
    public ngOnDestroy(): void {
        this.upgradeNodeCollection.destroy();
        this.updateStore.setIsSystemUpdateInProgressState(false);

        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();
    }

    /**
     * Opens the progress in the wizard.
     * If pre-checks are in progress, this will open the pre-checks progress in Wizard.
     * If the upgrade is in progress, this will open the transcript modal.
     */
    public openProgressModal(): void {
        if (this.preChecksInProgress) {
            this.sharePreChecksProgressToWizard(false, this.selectedSegIds);
        } else {
            this.openUpgradeTranscriptModal();
        }
    }

    /**
     * Return if the 'View Progress' button needs to be disabled.
     */
    public get disableViewProgressButton(): boolean {
        const {
            isControllerUpgrade,
            isControllerUpgradeInProgress,
            isNoUpgradeInProgress,
            isSeGroupUpgradeInProgress,
            preChecksInProgress,
        } = this;

        return isNoUpgradeInProgress ||
            isControllerUpgrade && !isControllerUpgradeInProgress && !preChecksInProgress ||
            !isControllerUpgrade && isSeGroupUpgradeInProgress;
    }

    /**
     * Return the header label for progress card.
     */
    public get progressCardHeaderLabel(): string {
        if (this.isRestoreFlow) {
            return this.l10nService.getMessage(
                this.l10nKeys.controllerRestoringToVersionHeader,
                [this.controllerVersion],
            );
        }

        if (this.isDryRunFlow) {
            return this.l10nService.getMessage(
                this.l10nKeys.dryrunInProgress,
            );
        }

        return this.l10nService.getMessage(
            this.l10nKeys.controllerUpdatingToVersionHeader,
            [this.controllerVersion],
        );
    }

    /**
     * Open modal to display the progress of upgrade/rollback operation.
     */
    private openUpgradeTranscriptModal(): void {
        const node = this.upgradeNodeCollection.getControllerNode();
        const { id, nodeType } = node;

        this.dialogService.add({
            id: UPGRADE_TRANSCRIPT_DIALOG_ID,
            component: UpgradeTranscriptModalComponent as Type<Component>,
            componentProps: {
                nodeId: id,
                nodeType,
                isDryRunFlow: this.isDryRunFlow,
                onClose: () => this.dialogService.remove(UPGRADE_TRANSCRIPT_DIALOG_ID),
            },
        });
    }

    /**
     * Once Update is triggered restart the polling for Update.
     */
    private async onUpdate(isWarning: boolean): Promise<void> {
        /**
         * Restart polling on warning state of pre-checks.
         * Success case is already handled by trackPreChecksProgress method.
         */
        if (isWarning) {
            await this.updateStore.triggerPreChecks(this.userSelectedOptions, true);
            this.updateStore.onPreChecksTrigger();
            await this.restartUpgradePolling(isWarning);
            this.setIsNoUpgradeInProgress();
        }
    }

    /**
     * share polling info with wizard to display the progress of pre-checks.
     */
    private async sharePreChecksProgressToWizard(
        readonly = false,
        selectedSegIds?: string[],
    ): Promise<void> {
        let upgradeNodeCollection;

        /**
         * Handle case where few SEGs are in upgrade state and few in pre-checks state.
         * Class level upgradeNodeCollection contains information about nodes in upgrade state so
         * we need to fetch information for pre-checks state nodes.
         */
        if (selectedSegIds &&
            intersection(
                this.upgradeNodeCollection.getItemIds(),
                selectedSegIds,
            ).length !== selectedSegIds.length) {
            upgradeNodeCollection = new this.UpgradeNodeCollectionClass();
            upgradeNodeCollection.setParams({
                pre_check_info: true,
                node_type: nodeTypesHash.NODE_SE_GROUP,
                dryrun: this.isDryRunFlow,
            });

            await upgradeNodeCollection.load();
            upgradeNodeCollection.stopUpdates();
        } else {
            upgradeNodeCollection = this.upgradeNodeCollection;
        }

        this.checkWizardStatus.emit({
            upgradeNodeCollection,
            readonly,
            selectedSegIds,
            currentUpdateWizardStage: UpdateWizardStage.PRE_CHECK,
            isRestoreFlow: this.isRestoreFlow,
        });
    }

    /**
     * Create instance of upgradeNodeCollection and set polling interval.
     */
    private initUpgradeNodeCollection(): void {
        this.upgradeNodeCollection = new this.UpgradeNodeCollectionClass({
            bind: {
                collectionLoadSuccess: this.updateProgressFlags,
            },
        });
        this.upgradeNodeCollection.setUpdateInterval(UPGRADE_STATE_POLLING_INTERVAL);
    }

    /**
     * Restart polling of upgrade status.
     */
    private async restartUpgradePolling(isWarning: boolean): Promise<void> {
        this.stopUpgradeStatusPolling();
        await this.triggerUpgradeStatusInfoPolling(isWarning);
    }

    /**
     * Stop polling of upgrade status api.
     */
    private stopUpgradeStatusPolling(): void {
        if (this.upgradeNodeCollection) {
            this.upgradeNodeCollection.stopUpdates();
        }
    }

    /**
     * Update all the progress related flags to be used to update the template.
     */
    private updateProgressFlags = (): void => {
        if (this.preChecksInProgress) {
            this.trackPreChecksProgress();
        } else {
            this.setControllerUpdateIsInProgress();
            this.setSeGroupsAreInProgress();
        }
    };

    /**
     * Set controller upgrade/dryrun percentage.
     */
    private setControllerUpgradePercentage(): void {
        if (this.isDryRunFlow) {
            this.controllerUpgradePercentage = this.upgradeNodeCollection
                .getControllerDryrunProgressPercentage() || 0;

            return;
        }

        this.controllerUpgradePercentage = this
            .upgradeNodeCollection
            .getControllerProgressPercentage() || 0;
    }

    /**
     * Set controller node's version. During upgrade in-progress operation, currentVersion
     * is updated to be the 'target version'.
     */
    private setControllerVersion(): void {
        const controllerNode = this.upgradeNodeCollection.getControllerNode();

        if (controllerNode) {
            const { preChecksInProgress } = this;

            if (preChecksInProgress) {
                this.controllerVersion = controllerNode
                    .getImageRefVersion() || this.rollbackVersion;
            } else {
                this.controllerVersion = controllerNode.getVersion();
            }
        }
    }

    /**
     * Set isControllerUpgradeInProgress flag.
     */
    private setControllerUpdateIsInProgress(): void {
        const {
            isDryRunFlow,
            upgradeNodeCollection,
        } = this;

        this.isControllerUpgradeInProgress =
            isDryRunFlow ? upgradeNodeCollection.isDryrunInProgress() :
                upgradeNodeCollection.isControllerUpdateInProgress();

        this.updateStore.setIsSystemUpdateInProgressState(this.isControllerUpgradeInProgress);

        if (this.isControllerUpgradeInProgress) {
            this.setControllerVersion();
            this.setControllerUpgradePercentage();
        } else if (this.isDryRunFlow && !this.isPreCheckFlow) {
            this.setDryrunNodes();
        }

        this.setIsNoUpgradeInProgress();
    }

    /**
     * Set isSeGroupUpgradeInProgress flag.
     */
    private setSeGroupsAreInProgress(): void {
        const { upgradeNodeCollection } = this;

        this.isSeGroupUpgradeInProgress = upgradeNodeCollection.isSeGroupUpdateInProgress();

        if (this.isSeGroupUpgradeInProgress) {
            this.seGroupCount = this.getSeGroupCount();
            this.setSegVersion();
        }

        this.setIsNoUpgradeInProgress();
    }

    /**
     * Return number of seGroups currently in-progress state.
     */
    private getSeGroupCount(): number {
        return this.upgradeNodeCollection.getSeGroupCount();
    }

    /**
     * Set isNoUpgradeInProgress flag.
     */
    private setIsNoUpgradeInProgress(): void {
        const {
            isControllerUpgradeInProgress,
            isSeGroupUpgradeInProgress,
            fipsOperational,
            fipsOperationalHash,
            preChecksInProgress,
        } = this;

        this.isNoUpgradeInProgress = !isControllerUpgradeInProgress &&
            !isSeGroupUpgradeInProgress &&
            fipsOperational !== fipsOperationalHash.FIPS_UNKNOWN &&
            !preChecksInProgress;
    }

    /**
     * Start polling on trigger of pre checks.
     */
    private async onPrechecksTrigger(): Promise<void> {
        this.preChecksInProgress = true;
        this.setIsNoUpgradeInProgress();

        await this.triggerUpgradeStatusInfoPolling(true);

        this.updateStore.setDryRunNodesState([]);

        /**
         * Prevent wizard from automatically opening if the user is on the Update page.
         * Progress will be shared with wizard only if the wizard is open.
         */
        if (this.isWizardOpen) {
            this.sharePreChecksProgressToWizard();
        }
    }

    private async triggerUpgradeStatusInfoPolling(isPreCheckFlow = false): Promise<void> {
        const { UPGRADE_STATE_GROUP_IN_PROGRESS } = UpgradeStateGroupTypesHash;
        const {
            NODE_CONTROLLER_CLUSTER,
            NODE_SE_GROUP,
        } = nodeTypesHash;
        const params: IUpgradeNodeCollectionParams = {
            pre_check_info: null,
            state: null,
            node_type: null,
            dryrun: null,
        };

        if (isPreCheckFlow) {
            params.pre_check_info = true;
        } else if (this.isDryRunFlow) {
            params.dryrun = true;
        } else {
            params.state = UPGRADE_STATE_GROUP_IN_PROGRESS;
        }

        if (!this.isControllerUpgrade) {
            params.node_type = NODE_SE_GROUP;
        } else if (
            this.isControllerUpgrade && (this.isSegUpgradeSelected || !isPreCheckFlow)
        ) {
            params.node_type = `${NODE_CONTROLLER_CLUSTER},${NODE_SE_GROUP}`;
        } else {
            params.node_type = NODE_CONTROLLER_CLUSTER;
        }

        this.upgradeNodeCollection.setParams(params);
        await this.upgradeNodeCollection.load();

        /**
         * When re triggering the pre-checks from the transcript modal.
         */
        if (isPreCheckFlow && !this.isPreCheckFlow) {
            this.updateStore.setIsPreCheckFlowState(true);
        }
    }

    /**
     * Continuously track the progress of pre-checks and update the flags accordingly.
     */
    private async trackPreChecksProgress(): Promise<void> {
        const {
            isControllerUpgrade,
            isSegUpgradeSelected,
            updateStore,
            upgradeNodeCollection,
        } = this;

        const preChecksInProgress = upgradeNodeCollection.arePreChecksInProgress();

        updateStore.onPreChecksLoad();

        if (preChecksInProgress) {
            if (isControllerUpgrade) {
                this.setControllerVersion();
            }

            if (isSegUpgradeSelected || !isControllerUpgrade) {
                this.setSegVersion();
            }
        } else {
            await this.updateOverallPreChecksState();
        }

        this.setIsNoUpgradeInProgress();
    }

    /**
     * Set pre-check Nodes.
     */
    private setPreCheckNodes(): void {
        const {
            isControllerUpgrade,
            selectedSegIds,
            updateStore,
            upgradeNodeCollection,
        } = this;

        let preCheckNodes: ITUpgradeNode[];

        if (isControllerUpgrade) {
            preCheckNodes = [upgradeNodeCollection.getControllerNode()];
        } else {
            preCheckNodes = upgradeNodeCollection.getSelectedSegNodes(selectedSegIds);
        }

        updateStore.setPreCheckNodesState(preCheckNodes);
    }

    /**
     * Set the version SEG is upgrading to.
     * In case of pre-checks, the version received from backend is with image_ref key while,
     * in case of an actual upgrade, the version is picked up from the complete version hash.
     */
    private setSegVersion(): void {
        const {
            preChecksInProgress,
            selectedSegIds,
            upgradeNodeCollection,
        } = this;

        let seGroupList: ITUpgradeNode[];

        if (selectedSegIds.length) {
            seGroupList = upgradeNodeCollection.getSelectedSegNodes(selectedSegIds);
        } else {
            seGroupList = upgradeNodeCollection.itemList as ITUpgradeNode[];
        }

        if (seGroupList?.length) {
            this.segVersion = preChecksInProgress ?
                seGroupList[0]?.getImageRefVersion() :
                seGroupList[0]?.getVersion();
        }
    }

    /**
     * Return the overall pre-checks status for all the nodes with latest pre-checks operation.
     */
    private getOverallPreChecksState(): UpgradeFsmState {
        const { upgradeNodeCollection } = this;

        return this.isControllerUpgrade ?
            upgradeNodeCollection.getOverallPreCheckState() :
            upgradeNodeCollection.getSelectedSegsOverallState(this.selectedSegIds);
    }

    /**
     * Update the overallPreChecks state on initial load.
     */
    private async updateOverallPreChecksState(): Promise<void> {
        const overallPreChecksState = this.getOverallPreChecksState();

        this.updateStore.onPreChecksComplete(overallPreChecksState);
        this.setPreCheckNodes();

        if (
            overallPreChecksState === UpgradeFsmState.UPGRADE_PRE_CHECK_SUCCESS ||
            this.isDryRunFlow &&
            overallPreChecksState === UpgradeFsmState.UPGRADE_PRE_CHECK_WARNING
        ) {
            await this.restartUpgradePolling(false);
            this.updateStore.setIsPreCheckFlowState(false);
        }

        this.updateStore.setOverallPreChecksState(this.getOverallPreChecksState());
    }

    /**
     * Set the dryRunNodes in the store.
     */
    private setDryrunNodes(): void {
        this.updateStore.setDryRunNodesState(
            [this.upgradeNodeCollection.getControllerNode()],
        );
    }
}
